import React, {useEffect, useState} from "react";
import {Layout, List, Popconfirm} from "antd";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";
import {MDBCol, MDBIcon, MDBRow} from "mdbreact";
import {Grid, Card, Text, Button, Tooltip} from "@nextui-org/react";
import {useToast} from "@chakra-ui/react";
import TransactionChart from "./TransactionChart";
import {useParams, useNavigate} from "react-router-dom";
import {Tag} from "primereact/tag";
import {Rating} from "primereact/rating";
import {useCollection, useDocumentData} from "react-firebase-hooks/firestore";
import {collection, doc, getFirestore, updateDoc, query, where} from "firebase/firestore";
import Firebase from "../../Firebase/Firebase";
import TransactionTable from "./TransactionTable";
import moment from "moment";
import {logAdminAction} from "../../utils/auditLog";


const db = getFirestore(Firebase);
const {Content} = Layout;

const statusSeverity = (s: string): "success"|"warning"|"danger"|"info" => {
    const l = (s ?? '').toLowerCase();
    if (l === 'active')    return 'success';
    if (l === 'suspended') return 'warning';
    if (l === 'disabled' || l === 'banned') return 'danger';
    return 'info';
};

const InfoRow = ({label, value}: {label: string; value: React.ReactNode}) => (
    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center',
                 padding:'9px 0', borderBottom:'1px solid #f5f5f5'}}>
        <span style={{fontSize:12, color:'#888', fontWeight:500, minWidth:110}}>{label}</span>
        <span style={{fontSize:13, color:'#222', fontWeight:500, textAlign:'right'}}>{value ?? '—'}</span>
    </div>
);

const BuyerDetails = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [collapsed, setCollapsed] = useState(false);
    const [buyer, setBuyer]         = useState<any>();
    const [reviewArray, setReviewArray]           = useState<any[]>([]);
    const [sellerArray, setSellerArray]           = useState<any[]>([]);
    const [productArray, setProductArray]         = useState<any[]>([]);
    const [transactionsArray, setTransactionsArray] = useState<any[]>([]);
    const [logsArray, setLogsArray]               = useState<any[]>([]);
    const [ordersArray, setOrdersArray]           = useState<any[]>([]);

    const uid = id?.trim() ?? '';

    const [value] = useDocumentData(doc(db, 'users', uid),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [reviewSnapshot] = useCollection(
        query(collection(db, 'reviews'), where('buyerId', '==', uid)),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [sellerSnapshot] = useCollection(
        query(collection(db, 'users'), where('type', '==', 'Seller')),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [prodSnapshot] = useCollection(collection(db, 'products'),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [orderSnapshot] = useCollection(
        query(collection(db, 'orders'), where('buyerId', '==', uid)),
        {snapshotListenOptions: {includeMetadataChanges: true}});

    function fmt(x: any) {
        return x != null ? Number(x).toLocaleString() : '0';
    }

    useEffect(() => {
        if (value) setBuyer(value);

        const sellers: any[] = [];
        sellerSnapshot?.docs.forEach(d => sellers.push(d.data()));
        setSellerArray(sellers);

        const prods: any[] = [];
        prodSnapshot?.docs.forEach(d => prods.push(d.data()));
        setProductArray(prods);

        const revs: any[] = [];
        reviewSnapshot?.docs.forEach(d => revs.push(d.data()));
        setReviewArray(revs);

        if (orderSnapshot) {
            const orders: any[] = [];
            orderSnapshot.docs.forEach(d => orders.push(d.data()));
            setOrdersArray(orders);
            setTransactionsArray(orders.map(o => ({
                seller:  o.sellerName ?? '—',
                product: o.items?.[0]?.productName ?? '—',
                amount:  `MK ${fmt(o.totalAmount)}`,
                status:  <Tag severity={(o.orderStatus==='pending')?'warning':(o.orderStatus==='completed')?'success':'info'}
                              value={o.orderStatus}/>,
                date:    moment(o.createdAt).format('DD MMM YYYY'),
                payment: o.paymentMethods,
            })));
        }
    }, [uid, value, reviewSnapshot, prodSnapshot, orderSnapshot, sellerSnapshot]);

    const changeStatus = (status: string) => {
        updateDoc(doc(db, 'users', uid), {userStatus: status}).then(() => {
            logAdminAction('user_status_changed', {
                targetId: uid, targetName: buyer?.fullName,
                targetType: 'user', from: buyer?.userStatus, to: status,
            });
            toast({position:'top', title:`User ${status}`, status:'success',
                   duration:2000, isClosable:true});
        }).catch((e: any) => {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        });
    };

    const isActive    = ['active','Active'].includes(buyer?.userStatus ?? '');
    const isSuspended = (buyer?.userStatus ?? '').toLowerCase() === 'suspended';

    const totalSpent = ordersArray
        .filter(o => o.orderStatus === 'completed' || o.orderStatus === 'delivered')
        .reduce((s, o) => s + (Number(o.totalAmount) || 0), 0);

    const stats = [
        {title:'Orders',   icon:<MDBIcon icon="shopping-cart" size="2x" style={{color:'#29327b'}} className="mb-2"/>, number: ordersArray.length},
        {title:'Completed',icon:<MDBIcon icon="check-circle" size="2x" style={{color:'#29327b'}} className="mb-2"/>, number: ordersArray.filter(o=>o.orderStatus==='completed'||o.orderStatus==='delivered').length},
        {title:'Reviews',  icon:<MDBIcon icon="star"         size="2x" style={{color:'#29327b'}} className="mb-2"/>, number: reviewArray.length},
        {title:'Spent',    icon:<MDBIcon icon="money-bill"   size="2x" style={{color:'#29327b'}} className="mb-2"/>, number: `MK ${fmt(totalSpent)}`},
    ];

    const joinDate = buyer?.dateAdded ?? buyer?.dateJoined;
    const joinFormatted = joinDate
        ? moment(joinDate?.toDate ? joinDate.toDate() : joinDate).format('DD MMM YYYY')
        : '—';

    return (
        <Layout style={{minHeight:'100vh'}}>
            <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
            <Layout className="site-layout">
                <Sidebar collapsed={collapsed}/>
                <Content>
                    <div className="mt-4 mx-4">

                        {/* ── Back + page title ─────────────────────────────── */}
                        <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:16}}>
                            <button onClick={() => navigate(-1)}
                                    style={{fontSize:12, color:'#29327b', border:'1px solid #c7d2fe',
                                            borderRadius:8, padding:'4px 14px', cursor:'pointer', background:'#eef2ff'}}>
                                ← Back
                            </button>
                            <span style={{fontWeight:700, fontSize:18, color:'#1a1a2e'}}>
                                {buyer?.fullName ?? 'Buyer'}
                            </span>
                            <Tag severity={statusSeverity(buyer?.userStatus ?? '')}
                                 value={buyer?.userStatus ?? '—'}/>
                        </div>

                        <MDBRow>
                            {/* ── Profile + info ─────────────────────────────── */}
                            <MDBCol md="9">
                                <div className="d-flex flex-row my-1" style={{gap:12}}>
                                    {/* Avatar */}
                                    <div style={{flexShrink:0, width:220, borderRadius:14, overflow:'hidden',
                                                 background:'#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}}>
                                        {buyer?.userImage
                                            ? <img src={buyer.userImage} alt="buyer"
                                                   style={{width:220, height:260, objectFit:'cover'}}/>
                                            : <MDBIcon icon="user-circle" size="5x" style={{color:'#ccc'}}/>}
                                    </div>

                                    {/* Details card */}
                                    <Card className="font-JosefinSans" style={{flex:1, overflow:'hidden'}}>
                                        <div style={{display:'flex', justifyContent:'space-between',
                                                     alignItems:'center', padding:'12px 16px',
                                                     borderBottom:'1px solid #f5f5f5'}}>
                                            <span style={{fontWeight:700, fontSize:14, color:'#29327b'}}>
                                                Buyer Details
                                            </span>
                                            <div style={{display:'flex', gap:8}}>
                                                {!isActive && (
                                                    <Popconfirm title="Activate this buyer?"
                                                                onConfirm={() => changeStatus('active')}
                                                                okText="Activate" cancelText="Cancel">
                                                        <button style={{fontSize:11, color:'#22c55e',
                                                                        border:'1px solid #bbf7d0', borderRadius:6,
                                                                        padding:'4px 12px', cursor:'pointer', background:'#f0fdf4'}}>
                                                            Activate
                                                        </button>
                                                    </Popconfirm>
                                                )}
                                                {!isSuspended && isActive && (
                                                    <Popconfirm title="Suspend this buyer? They can still log in but cannot place orders."
                                                                onConfirm={() => changeStatus('suspended')}
                                                                okText="Suspend" cancelText="Cancel"
                                                                okButtonProps={{danger:true}}>
                                                        <button style={{fontSize:11, color:'#f59e0b',
                                                                        border:'1px solid #fde68a', borderRadius:6,
                                                                        padding:'4px 12px', cursor:'pointer', background:'#fffbeb'}}>
                                                            Suspend
                                                        </button>
                                                    </Popconfirm>
                                                )}
                                                {isActive && (
                                                    <Popconfirm title="Disable this buyer? They will not be able to log in."
                                                                onConfirm={() => changeStatus('disabled')}
                                                                okText="Disable" cancelText="Cancel"
                                                                okButtonProps={{danger:true}}>
                                                        <button style={{fontSize:11, color:'#ef4444',
                                                                        border:'1px solid #fecaca', borderRadius:6,
                                                                        padding:'4px 12px', cursor:'pointer', background:'#fef2f2'}}>
                                                            Disable
                                                        </button>
                                                    </Popconfirm>
                                                )}
                                            </div>
                                        </div>

                                        <div style={{padding:'8px 16px'}}>
                                            <InfoRow label="Full Name"  value={buyer?.fullName}/>
                                            <InfoRow label="Email"      value={buyer?.emailAuth}/>
                                            <InfoRow label="Phone"      value={buyer?.phoneNumberAuth}/>
                                            <InfoRow label="Status"
                                                value={<Tag severity={statusSeverity(buyer?.userStatus ?? '')}
                                                            value={buyer?.userStatus ?? '—'}/>}/>
                                            <InfoRow label="Joined"     value={joinFormatted}/>
                                            <InfoRow label="Location"   value={buyer?.location ?? buyer?.city}/>
                                            <InfoRow label="Auth type"  value={buyer?.emailAuth ? 'Email' : 'Phone'}/>
                                        </div>
                                    </Card>
                                </div>
                            </MDBCol>

                            {/* ── Stat cards ─────────────────────────────────── */}
                            <MDBCol md="3">
                                <Grid.Container gap={1} justify="flex-start">
                                    {stats.map((item, i) => (
                                        <Grid xs={6} key={i}>
                                            <Card className="my-1 font-JosefinSans" style={{minHeight:100}}>
                                                <Card.Body>
                                                    <div className="d-flex flex-column justify-content-start px-1">
                                                        {item.icon}
                                                        <Text b size={12} className="mt-1">{item.title}</Text>
                                                        <Text b size={18} css={{color:'#29327b'}}>{item.number}</Text>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid.Container>
                            </MDBCol>
                        </MDBRow>

                        {/* ── Activity + Transactions / Reviews ─────────────── */}
                        <MDBRow className="mt-3">
                            <MDBCol md="8">
                                <Card className="my-2 font-JosefinSans overflow-hidden" isHoverable>
                                    <Card.Header>
                                        <Text b size={15} className="m-2">Activity (Past 6 Months)</Text>
                                    </Card.Header>
                                    <Card.Body>
                                        <TransactionChart logs={logsArray} orders={ordersArray}/>
                                    </Card.Body>
                                </Card>

                                <Card className="my-2 font-JosefinSans overflow-hidden" isHoverable>
                                    <Card.Header>
                                        <Text b size={15} className="m-2">Orders ({ordersArray.length})</Text>
                                    </Card.Header>
                                    <Card.Body>
                                        <TransactionTable data={transactionsArray}/>
                                    </Card.Body>
                                </Card>
                            </MDBCol>

                            <MDBCol md="4">
                                <Card className="my-2 font-JosefinSans overflow-hidden" isHoverable>
                                    <div style={{fontWeight:600, fontSize:14, padding:'12px 16px',
                                                 borderBottom:'1px solid #f5f5f5', color:'#29327b'}}>
                                        Reviews ({reviewArray.length})
                                    </div>
                                    <Card.Body>
                                        <List
                                            itemLayout="vertical"
                                            pagination={{pageSize:5, size:'small'}}
                                            dataSource={reviewArray}
                                            locale={{emptyText:'No reviews yet'}}
                                            renderItem={item => {
                                                const target = item.reviewType === 'seller'
                                                    ? sellerArray.find(x => x.Uid === item.objectId)?.fullName
                                                    : productArray.find(x => x.productId === item.objectId)?.name;
                                                const dateVal = item.createdAt?.toDate
                                                    ? item.createdAt.toDate() : item.createdAt;
                                                return (
                                                    <List.Item key={item.id}>
                                                        <div style={{display:'flex', gap:10, alignItems:'flex-start'}}>
                                                            <div style={{width:36, height:36, borderRadius:'50%',
                                                                         background:'#eef2ff', display:'flex',
                                                                         alignItems:'center', justifyContent:'center',
                                                                         color:'#29327b', fontWeight:700, fontSize:14, flexShrink:0}}>
                                                                {target?.charAt(0)?.toUpperCase() ?? '?'}
                                                            </div>
                                                            <div style={{flex:1, minWidth:0}}>
                                                                <div style={{fontWeight:600, fontSize:13,
                                                                             whiteSpace:'nowrap', overflow:'hidden',
                                                                             textOverflow:'ellipsis'}}>
                                                                    {target ?? 'Unknown'}
                                                                </div>
                                                                <div style={{display:'flex', gap:8, alignItems:'center', marginTop:2}}>
                                                                    <Rating value={item.rating} readOnly stars={5} cancel={false}/>
                                                                    <span style={{fontSize:11, color:'#aaa'}}>
                                                                        {dateVal ? moment(dateVal).format('DD MMM YYYY') : '—'}
                                                                    </span>
                                                                </div>
                                                                {item.description && (
                                                                    <p style={{fontSize:12, color:'#555', marginTop:4,
                                                                               overflow:'hidden', display:'-webkit-box',
                                                                               WebkitLineClamp:2, WebkitBoxOrient:'vertical'}}>
                                                                        {item.description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </List.Item>
                                                );
                                            }}
                                        />
                                    </Card.Body>
                                </Card>
                            </MDBCol>
                        </MDBRow>
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default BuyerDetails;
